import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { SummaryDiagnosticService } from './summary-diagnostic.service';
import { GtwElementsBaseUrlsModel } from '../../shared/_models/gtw-elements-base-urls.model';
import * as _ from 'lodash-es';
import { DashboardFilterService } from '../dashboard-filter/dashboard-filter.service';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
} from '../../shared/workspace-element-context';

import { safeJsonInput } from '../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-summary-diagnostic',
    templateUrl: './summary-diagnostic.component.html',
    styleUrls: ['./summary-diagnostic.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SummaryDiagnosticComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  diagName = 'Summary Diagnostics';
  label: string = 'Taxable Income';
  all_amount: number = 345;
  critical_amount: number = 0;
  infomational_amount: number = 0;
  data: any = {};
  globalParams: any = {};
  taxYears: any[] = [];
  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '',
    api: '/gtw',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: '',
  };
  GENERAL_CONFIG: any = {};
  options: any = {};
  dashboard: any = { components: [] };
  kpi: any = [];
  kpiData: any = [];
  clientKey: any;

  baseValue: string = '';
  baseLgValue: string = '';
  filterValue: string = '';
  filterBase(event: any) {
    console.log(event);
    this.filterValue = event?.queryStr ?? event?.query ?? event?.target?.value ?? this.baseValue ?? '';
    this.baseValue = this.filterValue;
  }

  clearFilterBase(): void {
    this.baseValue = '';
    this.filterValue = '';
  }

  loading: boolean = false;
  sheetCount: number = 0;
  checked: boolean = false;
  expand_collapse: boolean = false;

  @Output('load-detail-dashboard') loadDashboardScreen = new EventEmitter<any>();
  @Output('hide-resolved-diag') hideResolvedDiag = new EventEmitter<any>();
  @Output('expand-collapse') expandCollapseAll = new EventEmitter<any>();

  constructor(
    public summaryDiagnosticService: SummaryDiagnosticService,
    public dashboardFilterService: DashboardFilterService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private router: Router
  ) {
    this.subs.add(
      this.summaryDiagnosticService.sheetsLoaded.subscribe(
        (data: any) => {
          console.log('adfadfa', this.summaryDiagnosticService.dashboardPool);
          this.calculateKpi(data);
          this.cdr.markForCheck();
        },
        () => {}
      )
    );
  }

  ngOnInit(): void {
    // Per-screen hide of the workspace shell's <gtw-data-filters>. Mirrors
    // the pattern used by other CE screens (k2-sync, form-1118-schd-k/j,
    // k3-overrides, pdf-form-order). Restored to `false` in ngOnDestroy
    // so other tabs keep their filter bar — no global config change needed.
    this.gtwWorkspace.hideDataFilters.next(true);

    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.cdr.markForCheck();
      })
    );

    const pooled = this.summaryDiagnosticService.dashboardPool[0];
    if (
      !_.isEmpty(pooled) &&
      pooled.kpiData &&
      pooled.kpiData.length > 0
    ) {
      this.dashboard = pooled;
      this.kpiData = this.dashboard.kpiData;
      this.selectedKpi = pooled['selectedKpi'];
      this.selectedFilterParams = pooled['selectedFilterParams'];
      this.checked = pooled['hideResolvedDiag'];
      this.expand_collapse = pooled['expandCollapseAll'];
      this.kpiLoading = true;
      this.loading = true;
    } else if (
      !_.isEmpty(pooled) &&
      Array.isArray(pooled.dataSheets) &&
      pooled.dataSheets.length > 0 &&
      pooled.dataSheets.every((s: any) => s?.isLoaded === true)
    ) {
      // Returning to the tab after the previous instance was destroyed
      // mid-load: dashboardPool[0].dataSheets all completed (each `.then`
      // in loadSheets wrote back `isLoaded=true`), but the old component
      // was already torn down before `calculateKpi` finalized `kpiData`.
      // Rebuild `kpiData` locally from the cached `safeCopyArr` so both
      // loaders can drop straight away — without re-running any API.
      this.dashboard = pooled;
      this.selectedKpi = pooled['selectedKpi'] || 'All';
      this.selectedFilterParams = pooled['selectedFilterParams'];
      this.checked = pooled['hideResolvedDiag'] ?? this.checked;
      this.expand_collapse = pooled['expandCollapseAll'] ?? this.expand_collapse;
      this.kpiData = [];
      this.sheetCount = 0;
      for (const sheet of pooled.dataSheets) {
        this.calculateKpi(sheet);
      }
      this.kpiLoading = true;
      this.loading = true;
    }
  }

  ngOnDestroy(): void {
    // Re-show the workspace filter bar for any other tab the user may
    // navigate to next; without this, leaving this screen would leak the
    // hidden state and regress other screens that legitimately need the
    // global filters.
    this.gtwWorkspace.hideDataFilters.next(false);
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalParams = header.globalParams || {};
    this.clientKey = this.globalParams.client_key || '';
    this.taxYears = this.globalParams.tax_year ? [this.globalParams.tax_year] : [];
    this.cdr.markForCheck();
  }

  selectedKpi: any = 'All';
  kpiLoading: boolean = false;
  kpiFilter(kpi: any) {
    this.selectedKpi = kpi.DIAG_MSG;
    console.log('this.selectedKpi', this.selectedKpi);
    this.summaryDiagnosticService.dashboardPool[0]['selectedKpi'] = this.selectedKpi;
    this.cdr.markForCheck();
  }
  selectedFilterParams: any;
  loadDashbaord(event: any) {
    if (event.screen_key == 11461) {
      this.summaryDiagnosticService.drillDown.isDrillDown = false;
      this.summaryDiagnosticService.drillDown.selectedFilterParms = {};
      console.log('dashbaor adfadfadfadfevent', event);
      this.selectedKpi = 'All';
      this.baseValue = '';

      if (this.summaryDiagnosticService.dashboardPool.length > 0) {
        if (event.isFromFilter) {
          this.init(event);
          this.kpiData = [];
          this.kpiLoading = false;
        } else if (!_.isEqual(event, this.summaryDiagnosticService.dashboardPool[0].selectedFilterParams)) {
          this.init(event);
          this.kpiData = [];
          this.kpiLoading = false;
        }
      } else {
        this.init(event);
        this.kpiData = [];
        this.kpiLoading = false;
      }
      this.selectedFilterParams = event;
    }
    this.cdr.markForCheck();
  }
  init(filterParams: any) {
    this.loading = false;
    this.summaryDiagnosticService.getDashboardComponent(this.baseURLs.api, this.clientKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.options = data?.dashboard || {};
        this.dashboard = this.summaryDiagnosticService.createOrUpdateDashabord(
          this.options.dashboard_name,
          this.options.dashboard_id,
          this.options.data_sheets,
          this.options.components
        );
        this.summaryDiagnosticService.dashboardPool[0]['selectedKpi'] = this.selectedKpi;
        this.summaryDiagnosticService.dashboardPool[0]['selectedFilterParams'] = filterParams;
        this.checked = true;
        this.summaryDiagnosticService.dashboardPool[0]['hideResolvedDiag'] = true;
        this.summaryDiagnosticService.loadSheets(this.baseURLs.api, filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (d: any) => {
            this.dashboard = d;
            // `componentResolver` walks `dashboardPool[0].components` and
            // does `safeJsonInput<any>(ii.extraOptions, null, 'summary-diagnostic.parse1')` per component. If the API
            // returns a component with a null/undefined/malformed
            // `extraOptions`, the throw would skip the rest of this callback
            // — including `this.loading = true` — and the main loader would
            // stay forever. Swallow defensively: log, but still drop the
            // loader so the user sees the page chrome instead of an endless
            // spinner.
            try {
              this.componentResolver(this.dashboard);
            } catch (err) {
              console.error('componentResolver failed on dashboard payload', err);
            }
            this.loading = true;
            // Edge case: dashboard config has no data sheets — `sheetsLoaded`
            // will never fire so `calculateKpi`'s `dataSheets.length == sheetCount`
            // would never become true. Release the KPI loader immediately.
            const sheets = this.summaryDiagnosticService.dashboardPool[0]?.dataSheets;
            if (!Array.isArray(sheets) || sheets.length === 0) {
              this.kpiLoading = true;
            }
            this.cdr.markForCheck();
          },
          (err: any) => {
            // `loadSheets` currently returns `of(this.dashboardPool)` so this
            // path is hit only if the observable pipeline itself errors
            // (future-proofing — e.g. if it ever switches to `forkJoin` of
            // the sheet HTTPs and one of them rejects with a non-200 before
            // `.catch` swallows it). Match the outer error handler: drop
            // both loaders + markForCheck so OnPush actually picks it up.
            console.error('loadSheets pipeline error', err);
            this.loading = true;
            this.kpiLoading = true;
            this.cdr.markForCheck();
          }
        );
      },
      (error) => {
        // Without these, a 4xx/5xx from /getDashboardByKey would leave
        // `loading=false` forever and the main loader would never go away
        // (component is OnPush, so we also need markForCheck).
        console.error(error);
        this.loading = true;
        this.kpiLoading = true;
        this.cdr.markForCheck();
      },
      () => {
        console.log('dashboard Data api');
      }
    );
  }

  componentResolver(dashboardPool: any) {
    _.each(dashboardPool[0].components, function (com) {
      _.each(com, function (ii) {
        ii.extraOptionsJSON = safeJsonInput<any>(ii.extraOptions, null, 'summary-diagnostic.parse2');
        let sheet = _.find(dashboardPool[0].dataSheets, function (s) {
          return s.name == ii.name;
        });
        ii.sheet = sheet;
      });
    });
    this.dashboard = dashboardPool[0];
    this.summaryDiagnosticService.setDashboardPool(dashboardPool);
    this.cdr.markForCheck();
  }
  obj = { DIAG_MSG: '', count: 0 };
  calculateKpi(data: any) {
    console.log('this dashboar pool get', this.summaryDiagnosticService.getDashboardPool());
    this.sheetCount++;
    // lodash-es chain wrappers do not expose groupBy, so group the rows directly.
    const grouped = _.groupBy(data?.safeCopyArr || [], 'DIAG_MSG');
    const sumByValue = _.map(grouped, (items: any[], DIAG_MSG: string) => ({
      DIAG_MSG: DIAG_MSG,
      COUNT: items.length,
      EFFECTED_COUNT: items.length ? _.filter(items, (i) => i.FAILCNT > 0).length : 0,
      icon: DIAG_MSG == 'Critical' ? 'icon-alert-fill' : 'icon-information-fill',
    }));
    _.each(sumByValue, (i, key) => {
      if (this.kpiData.length == 0) {
        this.kpiData.push(sumByValue[key]);
      } else if (_.find(this.kpiData, { DIAG_MSG: sumByValue[key].DIAG_MSG }) == undefined) {
        this.kpiData.push(sumByValue[key]);
      } else {
        _.find(this.kpiData, (ii, k) => {
          if (ii.DIAG_MSG == sumByValue[key].DIAG_MSG) {
            ii['COUNT'] = ii['COUNT'] + sumByValue[key]['COUNT'];
            ii['EFFECTED_COUNT'] = ii['EFFECTED_COUNT'] + sumByValue[key]['EFFECTED_COUNT'];
            ii['effectedTemplate'] = ii.DIAG_MSG == 'Critical' ? 'criticalEffected' : 'InfoEffected';
            ii['totalTemplate'] = ii.DIAG_MSG == 'Critical' ? 'criticalTotal' : 'InfoTotal';
          }
        });
      }
    });
    if (this.summaryDiagnosticService.dashboardPool[0].dataSheets.length == this.sheetCount) {
      console.log('I am all KPI');
      let allKpiObj: any = { DIAG_MSG: 'All', icon: 'icon-spark-line-outline', COUNT: 0, EFFECTED_COUNT: 0 };
      let COUNT = 0;
      let EFFECTED_COUNT = 0;
      if (this.kpiData && this.kpiData.length > 0) {
        _.each(this.kpiData, (ii) => {
          if (ii.COUNT != 'undefined') {
            COUNT = COUNT + ii.COUNT;
            EFFECTED_COUNT = EFFECTED_COUNT + ii.EFFECTED_COUNT;
          }
        });
      }

      allKpiObj['COUNT'] = COUNT;
      allKpiObj['EFFECTED_COUNT'] = EFFECTED_COUNT;
      allKpiObj['effectedTemplate'] = 'allCount';
      allKpiObj['totalTemplate'] = 'allEffected';
      this.kpiData.unshift(allKpiObj);
      this.sheetCount = 0;
      this.kpiLoading = true;
    }
    console.log('this.kpiData', this.kpiData);
    this.summaryDiagnosticService.dashboardPool[0].kpiData = this.kpiData;
    this.cdr.markForCheck();
  }
  drillDownDeatil(event: any) {
    console.log('drillDown', event);

    // Fire the Angular @Output for any same-app Angular consumers that may
    // listen on the host custom element (kept for parity with the prior
    // contract — does not drive navigation).
    this.loadDashboardScreen.emit(event);

    // Navigate using the migrated workspace pattern: prepare workspace
    // state via the injected `WorkspaceService.loadLink(...)`, then drive
    // the URL with Angular `Router.navigate(...)`. This mirrors the
    // working drilldowns in:
    //   - efile/components/scrubbing-actions/efile-ctrlfn-dispatcher.service.ts
    //     (`diagnosticsDrilldownPlan` -> loadLink + getScreenNavigationCommands + router.navigate)
    //   - admin/components/form-view/form-view.component.ts
    //     (`onNavigateToDiagnostics`).
    //
    // The previous AngularJS-injector / `load-detail-dashboard` DOM bridge
    // path is not used in the migrated runtime: legacy `workspaceFactory`
    // never actually drove the Angular URL, so the bridge logs were
    // printing but the browser never navigated. Routing through
    // `WorkspaceService` + `Router` is what makes the URL change in
    // the migrated app.
    this.navigateDrillDown(event);
  }

  private navigateDrillDown(payload: any): void {
    try {
      const activityKey = payload?.activity_key != null ? String(payload.activity_key) : '';
      const screenKey = payload?.screen_key != null ? String(payload.screen_key) : '';
      if (!activityKey || !screenKey) {
        return;
      }
      const screenFilter = payload?.screenFilter ?? payload?.filter_json ?? [];
      const gridFilters = payload?.gridFilters ?? [];
      const actionTemplate = payload?.action_template_key ?? null;
      const drillDownRefresh = Number(payload?.drillDownRefresh ?? payload?.drilldown_filter_refresh ?? 0) === 1 ? 1 : 0;

      // Prepare workspace state (activeScreen, commentLink, filter
      // transfer, setTabActive emission). Same call shape as
      // `efile-ctrlfn-dispatcher.service.ts -> diagnosticsDrilldownPlan`.
      this.gtwWorkspace.loadLink(
        'drillDown',
        '{}',
        activityKey,
        screenKey,
        screenFilter,
        gridFilters,
        payload?.forceFilterReload ? { forceFilterReload: true } : null,
        actionTemplate,
        false,
        drillDownRefresh
      );

      // Drive the address bar via Angular Router so the migrated app
      // actually switches screens. `getScreenNavigationCommands` resolves
      // both standard `/workspace/<slug>` and top-level legacy states
      // (form-view, submissions-dasboard, ...).
      const active = this.gtwWorkspace.workspaceFactory?.activeScreen;
      if (!active) {
        return;
      }
      const commands = this.gtwWorkspace.getScreenNavigationCommands(active);
      if (!commands || commands.length === 0) {
        return;
      }
      this.router.navigate(commands, { queryParamsHandling: 'preserve' });
    } catch (err) {
      console.error('summary-diagnostic: drilldown navigation failed', err);
    }
  }
  isShow: boolean = true;

  showFilter() {
    this.isShow = !this.isShow;
    this.dashboardFilterService.notifyOther({ screen_key: 11461, isEnabled: true });
    this.cdr.markForCheck();
  }
  closeFilterPanel(event: any) {
    this.isShow = true;
    this.cdr.markForCheck();
  }

  toggle(event: MatSlideToggleChange) {
    console.log('toggled', event.checked);

    this.checked = event.checked;
    this.summaryDiagnosticService.dashboardPool[0]['hideResolvedDiag'] = this.checked;
    console.log('toggled', this.checked);
    this.cdr.markForCheck();
  }
  expandCollapse(event: MatSlideToggleChange) {
    console.log('expanded', event.checked);
    this.expand_collapse = event.checked;
    this.summaryDiagnosticService.dashboardPool[0]['expandCollapseAll'] = this.expand_collapse;
    this.cdr.markForCheck();
  }
}
